import React, { useState, useEffect } from 'react';
import './Navigation.css';
import { Link, useLocation } from 'react-router-dom';
import { isAuthenticated, clearAuthData } from '../utils/auth';
import { coreApi } from '../api/api';
import env from '../config/environments';

/* The Appli Network, wherever this build's network lives — `env.marketplaceUrl`
   is `http://localhost:3001` on the harness, the dev network on dev and the real
   one on prod. It used to be the production URL, hardcoded, so the dev build's
   second sidebar item walked a tester straight into production. A build with no
   network configured shows no item rather than a broken one. */
const JOBS_URL = env.marketplaceUrl ? `${env.marketplaceUrl.replace(/\/$/, '')}/jobs` : '';

const Navigation = () => {
    const location = useLocation();
    /* Read once, synchronously, so an authenticated page never paints the
       signed-out chrome for a frame before the effect below runs. */
    const [isLoggedIn, setIsLoggedIn] = useState(() => isAuthenticated());
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false); // Add this line

    useEffect(() => {
        // Check authentication status
        const checkAuthStatus = () => {
            setIsLoggedIn(isAuthenticated());
        };

        // Check on mount and location change
        checkAuthStatus();

        // Listen for auth state changes from other components
        window.addEventListener('authStateChange', checkAuthStatus);

        // Cleanup listener on unmount
        return () => {
            window.removeEventListener('authStateChange', checkAuthStatus);
        };
    }, [location]);

    const handleLogout = async () => {
        await coreApi.post('/auth/logout').catch(() => {});
        clearAuthData();
        window.location.href = '/';
    };

    // Add this function
    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    /* Signed out, the portal has two rooms — sign in and sign up — and neither
       of the four signed-in destinations is one of them: every one of them
       bounced a visitor straight back to /login. The chrome says what is
       actually reachable. */
    if (!isLoggedIn) {
        return (
            <div className={`vertical-navbar ${isMobileMenuOpen ? 'mobile-menu-active' : ''}`}>
                <button
                    className="mobile-menu-toggle"
                    onClick={toggleMobileMenu}
                    aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                    aria-expanded={isMobileMenuOpen}
                >
                    <span></span>
                    <span></span>
                    <span></span>
                </button>

                {/* Hidden on mobile by Navigation.css, which owns that decision. */}
                <div className="navbar-header">
                    <Link to="/login" className="navbar-brand">
                        <img
                            src="/images/appli-logo-white.png"
                            alt="Appli"
                            className="logo"
                        />
                    </Link>
                </div>

                <div className={`mobile-menu-overlay ${isMobileMenuOpen ? 'active' : ''}`} onClick={toggleMobileMenu}></div>
                <nav className={`navbar-nav ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
                    <Link
                        to="/login"
                        className={`nav-link ${location.pathname === '/login' ? 'active' : ''}`}
                        onClick={() => setIsMobileMenuOpen(false)}
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M15 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M10 17L15 12L10 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M15 12H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span className="nav-label">Log in</span>
                    </Link>
                    <Link
                        to="/signup"
                        className={`nav-link ${location.pathname === '/signup' ? 'active' : ''}`}
                        onClick={() => setIsMobileMenuOpen(false)}
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H6C4.93913 15 3.92172 15.4214 3.17157 16.1716C2.42143 16.9217 2 17.9391 2 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M19 8V14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M22 11H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span className="nav-label">Sign up</span>
                    </Link>
                </nav>
            </div>
        );
    }

    return (
        <div className={`vertical-navbar ${isMobileMenuOpen ? 'mobile-menu-active' : ''}`}>
            {/* Add this hamburger button */}
            <button
                className="mobile-menu-toggle"
                onClick={toggleMobileMenu}
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={isMobileMenuOpen}
            >
                <span></span>
                <span></span>
                <span></span>
            </button>

        {/* Hidden on mobile by Navigation.css, which owns that decision: read off
            `window.innerWidth` at render time, the mark vanished for good on any
            window that was ever narrow and never came back when it widened. */}
            <div className="navbar-header">
                <Link to="/" className="navbar-brand">
                    <img
                        src="/images/appli-logo-white.png"
                        alt="Appli"
                        className="logo"
                    />
                </Link>
            </div>
            {/* Add this overlay */}
            <div className={`mobile-menu-overlay ${isMobileMenuOpen ? 'active' : ''}`} onClick={toggleMobileMenu}></div>
            <nav className={`navbar-nav ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
                <Link
                    to="/"
                    className={`nav-link ${location.pathname === '/' ? 'active' : ''}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                >
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <path
                            d="M9 22V12H15V22"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <span className="nav-label">Dashboard</span>
                </Link>
                {JOBS_URL && <a
                    href={JOBS_URL}
                    className="nav-link"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsMobileMenuOpen(false)}
                >
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M16 4H18C18.5304 4 19.0391 4.21071 19.4142 4.58579C19.7893 4.96086 20 5.46957 20 6V18C20 18.5304 19.7893 19.0391 19.4142 19.4142C19.0391 19.7893 18.5304 20 18 20H6C5.46957 20 4.96086 19.7893 4.58579 19.4142C4.21071 19.0391 4 18.5304 4 18V6C4 5.46957 4.21071 4.96086 4.58579 4.58579C4.96086 4.21071 5.46957 4 6 4H8"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <path
                            d="M15 2H9C8.44772 2 8 2.44772 8 3V5C8 5.55228 8.44772 6 9 6H15C15.5523 6 16 5.55228 16 5V3C16 2.44772 15.5523 2 15 2Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <span className="nav-label">Jobs</span>
                    {/* It leaves the portal; the mark says so before the click does. */}
                    <svg className="nav-external" width="12" height="12" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
                        aria-hidden="true">
                        <path d="M7 17L17 7" />
                        <path d="M9 7h8v8" />
                    </svg>
                    <span className="nav-sr">(opens the Appli Network in a new tab)</span>
                </a>}
                {/* The certificate system had no way in from the portal chrome — every route
                    under /tests was URL-only. TestNav still owns navigation inside it. One
                    word, on purpose: the rail is 160px and "Tests & certificates" broke onto
                    two lines; the product calls these Appli certificates. */}
                <Link
                    to="/tests"
                    className={`nav-link ${location.pathname.startsWith('/tests') ? 'active' : ''}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                >
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M12 15C15.3137 15 18 12.3137 18 9C18 5.68629 15.3137 3 12 3C8.68629 3 6 5.68629 6 9C6 12.3137 8.68629 15 12 15Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <path
                            d="M8.5 13.8L7.5 21L12 18.5L16.5 21L15.5 13.8"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <span className="nav-label">Certificates</span>
                </Link>
                <Link
                    to="/profile"
                    className={`nav-link ${location.pathname === '/profile' ? 'active' : ''}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                >
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <path
                            d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <span className="nav-label">Profile</span>
                </Link>
            </nav>
            <div className={`navbar-footer ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
                <button className="nav-link logout-btn" onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }}>
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <path
                            d="M16 17L21 12L16 7"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <path
                            d="M21 12H9"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <span className="nav-label">Log out</span>
                </button>
            </div>
        </div>
    );
};

export default Navigation; 